import React from 'react'
import {
  Box,
  Button,
  Checkbox,
  Field,
  Flex,
  Heading,
  Input,
  Message,
  Modal,
  toast,
} from '@workwhile/ui'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { addRateSchema, AddRateSchema } from '../schemas/addRate'
import { useAddRateFlowContextValue } from '../context/AddRateFlowProvider'
import { useRuleDetailContextValue } from '../../../context/RuleDetailProvider'
import { useRuleDetailWorkflowsContextValue } from '../../../context/RuleDetailWorkflowsProvider'

export const AddRateFlowMain = () => {
  const {
    state: { id: ruleId },
  } = useRuleDetailContextValue()

  const {
    addRateFlow: { isOpen, close },
  } = useRuleDetailWorkflowsContextValue()

  const {
    state: {
      mutationState: { isPending, errorMessage },
    },
    actions: { submit },
  } = useAddRateFlowContextValue()

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
  } = useForm<AddRateSchema>({
    resolver: zodResolver(addRateSchema),
    defaultValues: {
      hourlyRateDollars: undefined,
      note: '',
      isEnabled: true,
    },
  })

  const onSubmit = handleSubmit((data) => {
    submit(
      {
        ruleId,
        hourlyRateCents: Math.round(data.hourlyRateDollars * 100),
        note: data.note,
        isEnabled: data.isEnabled,
      },
      {
        onSuccess: () => {
          toast.success('Rate added successfully', { position: 'top-center' })
          reset()
          close()
        },
      }
    )
  })

  const handleClose = () => {
    reset()
    close()
  }

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      position="top"
      loading={isPending}
      customCta={
        <Flex>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit}>
            Add
          </Button>
        </Flex>
      }
    >
      <Box
        as="form"
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault()
          onSubmit()
        }}
      >
        <Heading level={3} fontWeight="400" style={{ margin: 0 }}>
          Add rate
        </Heading>
        <Box mt={3}>
          <Field
            label="Hourly rate ($/hr)"
            error={errors.hourlyRateDollars?.message}
          >
            <Input
              {...register('hourlyRateDollars', { valueAsNumber: true })}
              type="number"
              step="0.01"
              placeholder="e.g. 16.50"
            />
          </Field>
        </Box>
        <Box mt={3}>
          <Field label="Note" error={errors.note?.message}>
            <Input {...register('note')} placeholder="Optional" />
          </Field>
        </Box>
        <Box mt={3}>
          <Controller
            name="isEnabled"
            control={control}
            render={({ field }) => (
              <Checkbox value={field.value} onChange={field.onChange}>
                Enabled
              </Checkbox>
            )}
          />
        </Box>
        {errorMessage ? (
          <Message variant="error" mt={3} borderRadius="small">
            {errorMessage}
          </Message>
        ) : null}
      </Box>
    </Modal>
  )
}
